import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { LocaleRouter } from "./components/LocaleRouter";
import { HreflangMeta } from "./components/HreflangMeta";
import { SeoHead } from "./components/SeoHead";
import { ThemeProvider } from "./contexts/ThemeContext";
import { trackMetaDownloadView, trackMetaPageView } from "./lib/meta";
// Eager: home must render Story synchronously so the visible hero seed is never
// replaced by the Suspense fallback (which would wipe the seed and tank LCP).
import Story from "./pages/Story";

const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));

const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Download = lazy(() => import("./pages/Download"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const GeminiApiKey = lazy(() => import("./pages/GeminiApiKey"));
const GroqApiKey = lazy(() => import("./pages/GroqApiKey"));
const ByoLlm = lazy(() => import("./pages/ByoLlm"));
const LiveNotes = lazy(() => import("./pages/LiveNotes"));
const LiveAgent = lazy(() => import("./pages/LiveAgent"));
const LiveTranslator = lazy(() => import("./pages/LiveTranslator"));

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackMetaPageView();
    if (location === "/download") trackMetaDownloadView();
  }, [location]);

  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Switch>
        <Route path={"/"} component={Story} />
        <Route path={"/about"} component={About} />
        <Route path={"/contact"} component={Contact} />
        <Route path={"/features"} component={Features} />
        <Route path={"/pricing"} component={Pricing} />
        <Route path={"/download"} component={Download} />
        <Route path={"/privacy-policy"} component={PrivacyPolicy} />
        <Route path={"/terms-of-service"} component={TermsOfService} />
        <Route path={"/enterprise"} component={Enterprise} />
        <Route path={"/blog"} component={Blog} />
        <Route path={"/blog/:slug"} component={BlogPost} />
        <Route path={"/gemini-api-key"} component={GeminiApiKey} />
        <Route path={"/groq-api-key"} component={GroqApiKey} />
        <Route path={"/byo-llm-setup"} component={ByoLlm} />
        <Route path={"/live-notes"} component={LiveNotes} />
        <Route path={"/live-agent"} component={LiveAgent} />
        <Route path={"/live-translator"} component={LiveTranslator} />
        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <LocaleRouter>
        <ThemeProvider
          defaultTheme="dark"
          // switchable
        >
          <TooltipProvider>
            <HreflangMeta />
            <SeoHead />
            <ScrollToTop />
            <Toaster />
            <AppRoutes />
          </TooltipProvider>
        </ThemeProvider>
      </LocaleRouter>
    </ErrorBoundary>
  );
}

export default App;
